import { useState } from "react";
import { ArrowUpRight, ArrowDownRight, ChevronDown } from "lucide-react";

const Cell = ({ label, value, tone = "", className = "", testId }) => (
  <div className={`sg-cell ${className}`}>
    <span className="sg-cell-label"><i className="sg-tick" />{label}</span>
    <span className={`sg-cell-value ${tone}`} data-testid={testId}>{value}</span>
  </div>
);

function ResultPanel({ result, id }) {
  const tp = result.type === "TP";
  return (
    <div className={`sg-result ${tp ? "is-tp" : "is-sl"}`} data-testid={`sg-result-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="sg-result-kicker"><i className="sg-tick" />RESULT UPDATE</span>
        {result.time_label && <span className="text-xs text-white/70">{result.time_label}</span>}
      </div>
      <p className="sg-result-title">{tp ? "TP HIT" : "Setup SL"}</p>
      <p className="sg-result-pips"><span className="sg-result-num">{result.pips_label}</span> <span className="sg-result-unit">PIPS</span></p>
      <div className="sg-result-rule" />
      <p className="sg-result-note">{tp ? "Enjoy Your Profit" : "Stop loss hit."}</p>
    </div>
  );
}

export function SignalEntryCard({ signal, isNew = false }) {
  const [open, setOpen] = useState(false);
  const { id, label, time_label, instrument, direction, entry_zone, tps = [], sl, result } = signal;
  const buy = String(direction).toUpperCase() === "BUY";
  const Arrow = buy ? ArrowUpRight : ArrowDownRight;
  const panelId = `sg-result-panel-${id}`;
  return (
    <article className="sg-card" data-testid={`sg-entry-${id}`}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="sg-kicker"><i className="sg-tick" />{isNew ? "NEW SIGNAL" : "SIGNAL"}</p>
          <p className="sg-id" data-testid={`sg-id-${id}`}>{label || id}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {time_label && <span className="text-sm text-white/75" data-testid={`sg-time-${id}`}>{time_label}</span>}
          {result && <span className={`sg-badge ${result.type === "TP" ? "is-tp" : "is-sl"}`} data-testid={`sg-badge-${id}`}>{result.type} HIT</span>}
        </div>
      </header>
      <div className="flex items-center gap-3 mt-3">
        <span className="sg-arrow"><Arrow className="h-5 w-5" strokeWidth={2.25} /></span>
        <p className="sg-title"><span className="text-white">{instrument}</span> <span className="sg-dir">{String(direction).toUpperCase()}</span></p>
      </div>
      <Cell label="ENTRY ZONE" value={`${entry_zone.from} – ${entry_zone.to}`} className="sg-cell-lg mt-3" testId={`sg-entry-zone-${id}`} />
      <div className="grid grid-cols-2 gap-2.5 mt-2.5">
        {tps.map((v, i) => <Cell key={i} label="TP" value={v} className="sg-cell-md" testId={`sg-tp${i + 1}-${id}`} />)}
      </div>
      <div className="sg-cell sg-cell-sl mt-2.5">
        <span className="sg-cell-label"><i className="sg-tick is-red" />SL</span>
        <span className="sg-cell-value text-[#FF3B4A]" data-testid={`sg-sl-${id}`}>{sl}</span>
      </div>
      <p className="text-center text-sm text-white/80 mt-3">Please Manage Your Risk</p>
      <button type="button" className="sg-toggle mt-3" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)} data-testid={`sg-toggle-${id}`}>
        <span className="font-semibold">{open ? "Hide Result" : "View Result"}</span>
        <span className="flex items-center gap-2.5 text-white/70">
          {!open && !result && <span className="text-sm font-normal">Awaiting result</span>}
          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <div id={panelId} role="region" hidden={!open} className="mt-3" data-testid={`sg-panel-${id}`}>
        {result ? <ResultPanel result={result} id={id} /> : <p className="sg-awaiting" data-testid={`sg-awaiting-${id}`}>Awaiting result</p>}
      </div>
    </article>
  );
}

export function SignalFeed({ signals }) {
  return (
    <div className="space-y-4" data-testid="sg-feed">
      {signals.map((s, i) => <SignalEntryCard key={s.id} signal={s} isNew={i === 0 && !s.result} />)}
    </div>
  );
}
